<!-- ProjectDetail.vue -->
<template>
  <div>
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h4>{{ project.name }}</h4>
      <button class="btn btn-primary btn-sm" @click="openAddEmailModal">
        + Добавить email
      </button>
    </div>
    <hr>

    <h5>Привязанные email-адреса</h5>

    <!-- Таблица email -->
    <div v-if="projectEmails.length === 0" class="text-center p-4">
      <p class="text-muted">Нет привязанных email</p>
    </div>

    <div v-else>
      <table class="table table-hover">
        <thead>
        <tr>
          <th scope="col">ID</th>
          <th scope="col">Email</th>
          <th scope="col">Дата создания</th>
          <th scope="col" style="width: 100px">Действие</th>
        </tr>
        </thead>
        <tbody>
        <tr v-for="email in projectEmails" :key="email.id">
          <td>{{ email.id }}</td>
          <td>{{ email.email }}</td>
          <td>{{ formatDate(email.createdAt) }}</td>
          <td>
            <button
                class="btn btn-danger btn-sm"
                @click="removeEmail(email.id)"
                title="Удалить из проекта"
            >
              Удалить
            </button>
          </td>
        </tr>
        </tbody>
      </table>
    </div>

    <!-- Модальное окно для добавления email -->
    <div class="modal fade" id="addEmailModal" tabindex="-1" ref="modal">
      <div class="modal-dialog">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Добавить email к проекту "{{ project.name }}"</h5>
            <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
          </div>
          <div class="modal-body">
            <!-- Список существующих email -->
            <div class="mb-3">
              <label class="form-label">Выберите email из списка</label>
              <select class="form-select" v-model="selectedEmailId">
                <option value="">-- Выберите email --</option>
                <option
                    v-for="email in availableEmails"
                    :key="email.id"
                    :value="email.id"
                >
                  {{ email.email }} (ID: {{ email.id }})
                </option>
              </select>
            </div>

            <div class="text-center">или</div>

            <!-- Создание нового email -->
            <div class="mt-3">
              <label class="form-label">Создать новый email</label>
              <input
                  type="email"
                  class="form-control"
                  v-model="newEmail"
                  placeholder="example@mail.com"
              />
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Отмена</button>
            <button type="button" class="btn btn-primary" @click="addEmailToProject">
              Добавить
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {defineProps, computed, ref, onMounted, watch, onUnmounted} from 'vue'
import {useProjectStore} from '@/store/projects'
import {useEmailMailingStore} from '@/store/emailMailing'
import {Modal} from 'bootstrap'

const props = defineProps({
  id: String,
})

const projectStore = useProjectStore()
const emailStore = useEmailMailingStore()

const project = computed(() => projectStore.item)
const projectEmails = computed(() => emailStore.currentProjectEmails)

const modal = ref(null)
let modalInstance = null
const selectedEmailId = ref('')
const newEmail = ref('')
const allEmails = ref([])

const formatDate = (dateString) => {
  if (!dateString) return ''
  const date = new Date(dateString)
  return date.toLocaleDateString('ru-RU')
}

const availableEmails = computed(() => {
  const boundEmailIds = new Set(projectEmails.value.map(e => e.id))
  return allEmails.value.filter(email => !boundEmailIds.has(email.id))
})

const loadData = async () => {
  try {
    // Загружаем данные параллельно для улучшения производительности
    await Promise.all([
      projectStore.getById(props.id),
      emailStore.getEmailsByProjectId(props.id)
    ])
  } catch (error) {
    console.error('Ошибка загрузки данных:', error)
  }
}

const loadAllEmails = async () => {
  try {
    await emailStore.getList(1, 1000)
    allEmails.value = emailStore.items
  } catch (error) {
    console.error('Ошибка загрузки email:', error)
  }
}

const openAddEmailModal = () => {
  selectedEmailId.value = ''
  newEmail.value = ''
  allEmails.value = []
  loadAllEmails()
  modalInstance?.show()
}

const addEmailToProject = async () => {
  try {
    let emailId = selectedEmailId.value

    if (!emailId && newEmail.value) {
      const created = await emailStore.create({email: newEmail.value})
      emailId = created.id
    }

    if (!emailId) {
      alert('Выберите email или введите новый')
      return
    }

    await emailStore.addProjectsToEmail(emailId, [Number(props.id)])
    await emailStore.getEmailsByProjectId(props.id)
    await projectStore.getById(props.id)

    modalInstance?.hide()

    selectedEmailId.value = ''
    newEmail.value = ''
    allEmails.value = []
    await loadAllEmails()

  } catch (error) {
    console.error('Ошибка при добавлении email:', error)
    alert(error.response?.data?.message || 'Ошибка при добавлении email')
  }
}

const removeEmail = async (emailId) => {
  if (confirm('Удалить email из проекта?')) {
    try {
      await emailStore.removeProjectsFromEmail(emailId, [Number(props.id)])
      await emailStore.getEmailsByProjectId(props.id)
      await projectStore.getById(props.id)
      allEmails.value = []
    } catch (error) {
      console.error('Ошибка при удалении email:', error)
      alert(error.response?.data?.message || 'Ошибка при удалении email')
    }
  }
}

// Очистка данных при смене проекта
const clearAllData = () => {
  // Очищаем store
  emailStore.clearProjectEmails()

  // Очищаем локальные данные
  allEmails.value = []
  selectedEmailId.value = ''
  newEmail.value = ''
}

// Следим за изменением ID и сразу очищаем данные
watch(() => props.id, async (newId) => {
  if (newId) {
    // Сразу очищаем все данные (старый список исчезнет мгновенно)
    clearAllData()

    // Затем загружаем новые данные
    await loadData()
  }
}, { immediate: true }) // immediate: true для начальной загрузки

onMounted(() => {
  if (modal.value) {
    modalInstance = new Modal(modal.value)
  }
})

// Очистка при размонтировании компонента
onUnmounted(() => {
  emailStore.clearProjectEmails()
})
</script>